import { useState, useEffect, useRef } from "react";
import Api from "../../../../shared/api/api";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import "../styles/AdminBlogs.css";
import "../styles/AdminBooks.css";
import "../styles/AdminEmailTemplates.css";

export default function AdminEmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const [emojiSearch, setEmojiSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("classic");

  const [isEditMode, setIsEditMode] = useState(false);

  const [templateName, setTemplateName] = useState("");
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [isActive, setIsActive] = useState(true);

  const [previewHtml, setPreviewHtml] = useState("");

  const quillRef = useRef(null);

  /* -------------------------------------------
     MERGE TAG VARIABLES
  ------------------------------------------- */
  const templateVariables = [
    { name: "Customer Name", value: "{{customer_name}}" },
    { name: "Tracking Recipient Name", value: "{{name}}" },
    { name: "Order Number", value: "{{order_number}}" },
    { name: "Book Title", value: "{{book_title}}" },
    { name: "Book Format", value: "{{format}}" },
    { name: "Release Date", value: "{{release_date}}" },
    { name: "Order Date", value: "{{order_date}}" },
    { name: "Tracking Number", value: "{{tracking_number}}" },
    { name: "Download Link", value: "{{download_link}}" },
    { name: "Store Name", value: "{{store_name}}" },
    { name: "Contact Name", value: "{{contact_name}}" },
    { name: "Store City", value: "{{city}}" },
    { name: "Store State", value: "{{state}}" },
    { name: "Books Offered", value: "{{books_offered}}" },
    { name: "Bookstore Prices and MSRP", value: "{{book_pricing}}" },
    { name: "Author Name", value: "{{author_name}}" },
    { name: "Publisher Name", value: "{{publisher_name}}" },
    { name: "Author Website", value: "{{author_website}}" },
  ];

  const insertVariableAtCursor = (variable) => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const range = editor.getSelection(true);
    if (range) {
      editor.insertText(range.index, variable);
      editor.setSelection(range.index + variable.length);
      setBodyHtml(editor.root.innerHTML);
    }
  };

  /* -------------------------------------------
     EMOJI SYSTEM (copied from Newsletter.js)
  ------------------------------------------- */
  const emojiCategories = {
    classic: ["😀", "😃", "😄", "😁", "😆", "😅", "😂", "🤣", "🙂", "😉"],
    expressive: ["😊", "😇", "😍", "🤩", "😘", "😎", "🤔", "😐", "😶", "🙄", "😏", "🥺"],
    dramatic: ["😤", "😠", "😡", "😢", "😭", "😱", "😨", "😓", "😥"],
    celebration: ["🔥", "✨", "💫", "⚡", "🎉", "🎊", "🏆", "🌟", "🌙", "⭐"],
    writing: ["📚", "✍️", "📝", "✒️", "🎧", "🕰️", "🧭", "🧩", "🔍"],
    fantasy: ["🐉", "🗡️", "🛡️", "🔮", "🧙‍♂️", "🧝‍♂️", "🧛‍♂️", "👁️"],
    utility: ["📸", "🎬", "🎵", "💡", "💬", "📣", "👀", "🌄"],
  };

  const getFilteredEmojis = () => {
    let list = emojiCategories[activeCategory];
    if (!emojiSearch.trim()) return list;
    return list.filter((em) =>
      em.toLowerCase().includes(emojiSearch.toLowerCase())
    );
  };

  const insertEmojiAtCursor = (emoji) => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const range = editor.getSelection(true);
    if (range) {
      editor.insertText(range.index, emoji);
      editor.setSelection(range.index + emoji.length);
      setBodyHtml(editor.root.innerHTML);
    }
  };

  /* -------------------------------------------
     QUILL MODULES
  ------------------------------------------- */
  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ align: [] }],
      ["link"],
      ["clean"],
    ],
  };

  /* ----------------------------------------------------
     LOAD ALL TEMPLATES
  ---------------------------------------------------- */
  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const data = await Api.getAllEmailTemplates();
      setTemplates(data || []);
    } catch (err) {
      console.error("Error loading templates:", err);
    }
    setLoading(false);
  };

  /* ----------------------------------------------------
     OPEN CREATE MODAL
  ---------------------------------------------------- */
  const openCreateModal = () => {
    setIsEditMode(false);
    setTemplateName("");
    setSubject("");
    setBodyHtml("");
    setBodyText("");
    setIsActive(true);
    setShowModal(true);
  };

  /* ----------------------------------------------------
     OPEN EDIT MODAL
  ---------------------------------------------------- */
  const openEditModal = async (tpl) => {
    try {
      const full = await Api.getEmailTemplate(tpl.template_name);

      setIsEditMode(true);
      setTemplateName(full.template_name);
      setSubject(full.subject || "");
      setBodyHtml(full.body_html || "");
      setBodyText(full.body_text || "");
      setIsActive(full.is_active === 1);

      setShowModal(true);
    } catch (err) {
      console.error("Error loading full template:", err);
    }
  };

  /* ----------------------------------------------------
     OPEN PREVIEW MODAL
  ---------------------------------------------------- */
  const openPreviewModal = async (tpl) => {
    try {
      const full = await Api.getEmailTemplate(tpl.template_name);
      setPreviewHtml(full.body_html || "<p>(No HTML content)</p>");
      setShowPreview(true);
    } catch (err) {
      console.error("Preview load error:", err);
      alert("Failed to load preview.");
    }
  };

  /* ----------------------------------------------------
     SAVE TEMPLATE
  ---------------------------------------------------- */
  const saveTemplate = async () => {
    if (!templateName.trim()) return alert("Template name required.");
    if (!subject.trim()) return alert("Subject required.");

    try {
      if (isEditMode) {
        await Api.updateEmailTemplate(templateName, {
          subject,
          body_html: bodyHtml,
          body_text: bodyText || null,
          is_active: isActive ? 1 : 0,
        });
      } else {
        await Api.createEmailTemplate({
          template_name: templateName,
          subject,
          body_html: bodyHtml,
          body_text: bodyText || null,
          is_active: isActive ? 1 : 0,
        });
      }

      setShowModal(false);
      loadTemplates();
    } catch (err) {
      console.error("Save Error:", err);
      alert("Failed to save template.");
    }
  };

  /* ----------------------------------------------------
     TOGGLE ACTIVE STATE
  ---------------------------------------------------- */
  const toggleActive = async (tpl) => {
    try {
      const full = await Api.getEmailTemplate(tpl.template_name);

      await Api.updateEmailTemplate(tpl.template_name, {
        subject: full.subject,
        body_html: full.body_html,
        body_text: full.body_text,
        is_active: full.is_active ? 0 : 1,
      });

      loadTemplates();
    } catch (err) {
      console.error("Toggle Error:", err);
      alert("Failed to toggle template state.");
    }
  };

  /* ----------------------------------------------------
     CLOSE MODALS
  ---------------------------------------------------- */
  const closeModal = () => {
    setShowModal(false);
    setIsEditMode(false);
  };

  const closePreview = () => {
    setShowPreview(false);
    setPreviewHtml("");
  };

  /* ----------------------------------------------------
     RENDER UI
  ---------------------------------------------------- */
  return (
    <div className="adminPageContainer email-templates-page">
      <h1 className="text-2xl font-bold mb-4">Email Templates</h1>

      <button className="addBookBtn" onClick={openCreateModal}>
        + Create Template
      </button>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <table className="adminTable">
          <thead>
            <tr>
              <th>Name</th>
              <th>Subject</th>
              <th>Status</th>
              <th>Updated</th>
              <th style={{ width: "260px" }}>Actions</th>
            </tr>
          </thead>

          <tbody>
            {templates.map((tpl) => (
              <tr key={tpl.id}>
                <td>{tpl.template_name}</td>
                <td>{tpl.subject}</td>
                <td>{tpl.is_active ? "Active" : "Inactive"}</td>
                <td>{new Date(tpl.updated_at).toLocaleString()}</td>

                <td>
                  <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                    <button
                      className="templateEditBtn"
                      onClick={() => openPreviewModal(tpl)}
                    >
                      Preview
                    </button>

                    <button
                      className="templateEditBtn"
                      onClick={() => openEditModal(tpl)}
                    >
                      Edit
                    </button>

                    <button
                      className={tpl.is_active ? "templateDeleteBtn" : "templateEditBtn"}
                      onClick={() => toggleActive(tpl)}
                    >
                      {tpl.is_active ? "Deactivate" : "Activate"}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>

        </table>
      )}

      {/* ---------------------------------------
          PREVIEW MODAL
      --------------------------------------- */}
      {showPreview && (
        <div className="modalOverlay">
          <div className="modalContent previewModal">
            <h2>Email Preview</h2>

            <iframe title="Email template preview" sandbox="" srcDoc={previewHtml}
              className="previewContainer"
            />

            <div className="buttonRow">
              <button className="cancelBtn" onClick={closePreview}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------------------------------------
          CREATE / EDIT MODAL
      --------------------------------------- */}
      {showModal && (
        <div className="modalOverlay">
          <div className="modalContent">
            <h2>{isEditMode ? "Edit Template" : "Create Template"}</h2>

            {!isEditMode && (
              <>
                <label>Template Name</label>
                <input
                  type="text"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                />
              </>
            )}

            <label>Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />

            {/* ------------ VARIABLES DROPDOWN ------------ */}
            <select
              className="variableDropdown"
              onChange={(e) => {
                if (e.target.value !== "") {
                  insertVariableAtCursor(e.target.value);
                  e.target.value = "";
                }
              }}
            >
              <option value="">Insert Variable…</option>
              {templateVariables.map((v) => (
                <option key={v.value} value={v.value}>
                  {v.name}
                </option>
              ))}
            </select>

            {/* ------------ EMOJI BUTTON & PICKER ------------ */}
            <button
              type="button"
              className="emojiToggleBtn"
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            >
              😀 Emoji
            </button>

            {showEmojiPicker && (
              <div className="emojiPickerContainer">
                <input
                  type="text"
                  className="emojiSearchInput"
                  placeholder="Search emoji…"
                  value={emojiSearch}
                  onChange={(e) => setEmojiSearch(e.target.value)}
                />

                <div className="emojiCategoryTabs">
                  {Object.keys(emojiCategories).map((cat) => (
                    <div
                      key={cat}
                      className="emojiCategoryTab"
                      style={{
                        background: activeCategory === cat ? "#e3d9f8" : "#f1ecfa",
                      }}
                      onClick={() => setActiveCategory(cat)}
                    >
                      {cat.charAt(0).toUpperCase() + cat.slice(1)}
                    </div>
                  ))}
                </div>

                <div className="emojiGrid">
                  {getFilteredEmojis().map((em, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className="emojiBtn"
                      onClick={() => insertEmojiAtCursor(em)}
                    >
                      {em}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ------------ QUILL EDITOR ------------ */}
            <label>HTML Version</label>
            <ReactQuill
              ref={quillRef}
              value={bodyHtml}
              onChange={setBodyHtml}
              modules={quillModules}
              formats={[
                "header",
                "bold",
                "italic",
                "underline",
                "list",
                "bullet",
                "align",
                "link",
              ]}
              style={{ height: "270px", marginBottom: "20px" }}
            />

            <label>Plain Text Version (Optional)</label>
            <textarea
              className="newsletterTextarea"
              value={bodyText}
              onChange={(e) => setBodyText(e.target.value)}
            />

            <label className="checkboxRow">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
              Active
            </label>

            <div className="buttonRow">
              <button className="publishingSubmitBtn" onClick={saveTemplate}>
                Save Template
              </button>
              <button className="cancelBtn" onClick={closeModal}>
                Cancel
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}
